import React from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import PCNode, { AlternativeNodeColour } from '../Nodes/PCNode';
import * as PCConstants from '../PCConstants';
import * as GConstants from '../GConstants';
import { Model } from '../../hooks/useModelState';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';
import { IntersectionData } from '../intersectionTypes';
import processIntersections, { getNodeScreenDistance } from '../intersectionUtils';

const getNodeColor = (nodeColour: string): string => {
  if (nodeColour === AlternativeNodeColour.CONNECT) return GConstants.GREEN;
  if (nodeColour === AlternativeNodeColour.DISCONNECT) return GConstants.RED;
  if (nodeColour === AlternativeNodeColour.DELETE) return GConstants.RED;
  if (nodeColour === AlternativeNodeColour.TONNAGE) return GConstants.PC_BLUE;
  if (!nodeColour || nodeColour === AlternativeNodeColour.DEFAULT) return PCConstants.NODE_COLOUR;
  return nodeColour;
};

interface InstancedNodeMeshProps {
  nodes: PCNode[];
  hoveredNodeId: string | null;
  onHover: (node: PCNode | null) => void;
  onSelect: (node: PCNode) => void;
  onFocus: (node: PCNode, worldPosition?: THREE.Vector3) => void;
  onIntersections?: (intersections: IntersectionData[]) => void;
  model: Model;
  offset: THREE.Vector3;
  scale: number;
  colourOverrides?: Record<string, string>;
}

const tempMatrix = new THREE.Matrix4();
const tempColor = new THREE.Color();

const InstancedNodeMesh = React.memo(({
  nodes,
  hoveredNodeId,
  onHover,
  onSelect,
  onFocus,
  onIntersections,
  model,
  offset,
  scale,
  colourOverrides,
}: InstancedNodeMeshProps) => {
  const visualRef = React.useRef<THREE.InstancedMesh>(null);
  const hitboxRef = React.useRef<THREE.InstancedMesh>(null);
  const lastZoomRef = React.useRef<number>(0);
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);

  const count = Math.max(nodes.length, 1);

  const sharedGeo = React.useMemo(
    () => new THREE.SphereGeometry(1, 6, 6),
    [],
  );

  const nodeIdByIndex = React.useMemo(
    () => nodes.map((n) => n.id),
    [nodes],
  );

  const instanceData = React.useMemo(
    () => nodes.map((node) => {
      const [x, y, z] = cadToThreeJsPositionWithOffset(node.coords, offset, scale);
      const s = node.nodeSize * scale;
      const matrix = new THREE.Matrix4().makeScale(s, s, s).setPosition(x, y, z);
      const color = new THREE.Color(colourOverrides?.[node.id] ?? getNodeColor(node.nodeColour));
      return { matrix, color, nodeId: node.id, position: [x, y, z] as [number, number, number], size: s };
    }),
    [nodes, offset, scale, colourOverrides],
  );

  React.useLayoutEffect(() => {
    if (!visualRef.current || !hitboxRef.current) return;
    const visual = visualRef.current;
    const hitbox = hitboxRef.current;

    instanceData.forEach((item, i) => {
      visual.setMatrixAt(i, item.matrix);
      hitbox.setMatrixAt(i, item.matrix);

      const color = item.nodeId === hoveredNodeId
        ? tempColor.set(PCConstants.HOVER_COLOUR)
        : item.color;
      visual.setColorAt(i, color);
    });

    // Hide unused instances when count > nodes.length
    if (nodes.length === 0) {
      tempMatrix.makeScale(0, 0, 0);
      visual.setMatrixAt(0, tempMatrix);
      hitbox.setMatrixAt(0, tempMatrix);
      visual.setColorAt(0, tempColor.set('#000000'));
    }

    visual.instanceMatrix.needsUpdate = true;
    hitbox.instanceMatrix.needsUpdate = true;
    visual.boundingSphere = null;
    hitbox.boundingSphere = null;
    if (visual.instanceColor) visual.instanceColor.needsUpdate = true;
    lastZoomRef.current = 0;
    invalidate();
  }, [instanceData, hoveredNodeId, nodes.length, invalidate]);

  useFrame(() => {
    if (!hitboxRef.current || instanceData.length === 0) return;
    const zoom = (camera as THREE.OrthographicCamera).zoom ?? 1;
    if (zoom === lastZoomRef.current) return;
    lastZoomRef.current = zoom;
    const multiplier = Math.max(1, 5 / Math.sqrt(zoom));
    instanceData.forEach((item, i) => {
      const s = item.size * multiplier;
      tempMatrix.makeScale(s, s, s).setPosition(item.position[0], item.position[1], item.position[2]);
      hitboxRef.current!.setMatrixAt(i, tempMatrix);
    });
    hitboxRef.current.instanceMatrix.needsUpdate = true;
    hitboxRef.current.boundingSphere = null;
  });

  const getNodeByInstanceId = React.useCallback(
    (instanceId: number | undefined): PCNode | null => {
      if (instanceId == null || instanceId < 0 || instanceId >= nodes.length) return null;
      return nodes[instanceId];
    },
    [nodes],
  );

  const handleClick = React.useCallback((e: any) => {
    e.stopPropagation();
    if (onIntersections && e.intersections) {
      const intersectionData = processIntersections({
        intersections: e.intersections,
        model,
        offset,
        scale,
        camera: e.camera,
        pointer: e.pointer,
      });
      onIntersections(intersectionData);
      return;
    }
    const node = getNodeByInstanceId(e.instanceId);
    if (node) onSelect(node);
  }, [onIntersections, model, offset, scale, getNodeByInstanceId, onSelect]);

  const handleDoubleClick = React.useCallback((e: any) => {
    e.stopPropagation();
    const node = getNodeByInstanceId(e.instanceId);
    if (!node) return;
    const [x, y, z] = cadToThreeJsPositionWithOffset(node.coords, offset, scale);
    onFocus(node, new THREE.Vector3(x, y, z));
  }, [getNodeByInstanceId, offset, scale, onFocus]);

  const handlePointerOver = React.useCallback((e: any) => {
    e.stopPropagation();
    if (e.nativeEvent?.buttons) return;

    const clickedNode = getNodeByInstanceId(e.instanceId);
    if (!clickedNode) return;

    if (e.camera && e.pointer && e.intersections) {
      const seen = new Set<string>();
      const nodeIntersections = e.intersections.filter((i: THREE.Intersection) => {
        const obj = i.object as THREE.InstancedMesh;
        const nId = obj.userData?.instancedNodes
          ? nodeIdByIndex[i.instanceId ?? -1]
          : obj.userData?.nodeId;
        if (!nId || !model.nodes[nId] || seen.has(nId)) return false;
        seen.add(nId);
        return true;
      });

      if (nodeIntersections.length > 1) {
        let closestNode = clickedNode;
        let closestDist = Infinity;
        nodeIntersections.forEach((i: THREE.Intersection) => {
          const obj = i.object as THREE.InstancedMesh;
          const nId = obj.userData?.instancedNodes
            ? nodeIdByIndex[i.instanceId ?? -1]
            : obj.userData?.nodeId;
          const n = nId ? model.nodes[nId] : undefined;
          if (!n) return;
          const dist = getNodeScreenDistance(n, offset, scale, e.camera, e.pointer);
          if (dist < closestDist) {
            closestDist = dist;
            closestNode = n;
          }
        });
        onHover(closestNode);
        return;
      }
    }

    onHover(clickedNode);
  }, [getNodeByInstanceId, nodeIdByIndex, model.nodes, offset, scale, onHover]);

  const handlePointerOut = React.useCallback((e: any) => {
    e.stopPropagation();
    if (e.nativeEvent?.buttons) return;
    onHover(null);
  }, [onHover]);

  if (nodes.length === 0) return null;

  return (
    <>
      <instancedMesh
        ref={visualRef}
        args={[sharedGeo, undefined, count]}
        userData={{ instancedNodes: true, nodeIdByIndex }}
      >
        <meshBasicMaterial />
      </instancedMesh>
      <instancedMesh
        ref={hitboxRef}
        args={[sharedGeo, undefined, count]}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onDoubleClick={handleDoubleClick}
        visible={false}
        userData={{ instancedNodes: true, nodeIdByIndex, doubleClickHitbox: true }}
      >
        <meshBasicMaterial transparent opacity={0} />
      </instancedMesh>
    </>
  );
});

export default InstancedNodeMesh;
